import React, { useState } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { CountrySelect } from '../../../core/components/CountrySelect';
import { EmployeeRangeSelect } from '../../../core/components/EmployeeRangeSelect';
import { api } from '../../../core/utils/apiClient';

interface CompanyFields {
  id: number;
  name?: string;
  description?: string;
  website?: string;
  domain?: string;
  industry?: string;
  location?: string;
  founding_year?: number;
  employees?: string;
}

interface Props {
  company: CompanyFields;
  onClose: () => void;
  onSaved: (updated: any) => void;
  accentColor?: string;
}

export const EditCompanyModal: React.FC<Props> = ({
  company,
  onClose,
  onSaved,
  accentColor = COLORS.user.primary,
}) => {
  const [form, setForm] = useState<Partial<CompanyFields>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const val = <K extends keyof CompanyFields>(key: K): any =>
    form[key] !== undefined ? form[key] : company[key] ?? '';

  const set = (key: keyof CompanyFields, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        name: val('name'),
        description: val('description'),
        website: val('website'),
        domain: val('domain'),
        industry: val('industry'),
        location: val('location'),
        founding_year: val('founding_year') ? Number(val('founding_year')) : undefined,
        employees: val('employees'),
      };
      const response = await api.put(`/api/v1/companies/${company.id}`, payload);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save changes');
      }
      const updated = await response.json();
      onSaved(updated);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Edit Company</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input type="text" value={val('name')} onChange={(e) => set('name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={val('description')} onChange={(e) => set('description', e.target.value)}
              rows={4} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="text" value={val('website')} onChange={(e) => set('website', e.target.value)}
                placeholder="https://example.com" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input type="text" value={val('domain')} onChange={(e) => set('domain', e.target.value)}
                placeholder="example.com" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input type="text" value={val('industry')} onChange={(e) => set('industry', e.target.value)}
                placeholder="e.g., Technology, Finance" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <CountrySelect value={val('location')} onChange={(v) => set('location', v)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Founding Year</label>
              <input type="number" value={val('founding_year')} onChange={(e) => set('founding_year', e.target.value)}
                placeholder="e.g., 2020" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ borderColor: '#E5E7EB' }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employees</label>
              <EmployeeRangeSelect value={val('employees')} onChange={(v) => set('employees', v)} />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <button onClick={handleSave} disabled={isSaving}
            className="flex-1 px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: accentColor }}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={onClose} disabled={isSaving}
            className="px-4 py-2 rounded-lg font-medium border hover:bg-gray-50"
            style={{ borderColor: '#E5E7EB', color: '#374151' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
